import { useGroupsContext } from '@fs/zion-tree-groups'
import useFocusPerson from '../data-service/useFocusPerson'

export type PersonVisibility = {
  /** The focus person is withheld from the public, in a tree where that actually means something. */
  isHidden: boolean
  /** Hiding and showing a person are only meaningful here. A private tree is already invisible. */
  isPublicResearcherTree: boolean
}

/**
 * Resolves the public visibility of the focus person for the Hide/Show Person feature.
 *
 * Hiding withholds a person in a public researcher tree from everyone except that tree's
 * contributors and admins.
 *
 * Whether a person who is not hidden is instead confidential is a separate question, answered by
 * `getDisabledReason` in `HidePersonMenuItem` — the only place that needs it.
 */
export default function usePersonVisibility(): PersonVisibility {
  const { focusPerson } = useFocusPerson()
  const { treeDetails } = useGroupsContext()

  const isPublicResearcherTree =
    treeDetails?.researcherTree === true &&
    treeDetails.private === false &&
    Boolean(focusPerson?.treeId) &&
    treeDetails.treeId === focusPerson?.treeId

  // A person stays unpublished after the tree is made private, where the state no longer means anything,
  // so the tree has the final say. Living people are excluded because they are already hidden from the public.
  const isHidden = focusPerson?.unpublished === true && !focusPerson?.living && isPublicResearcherTree

  return { isHidden, isPublicResearcherTree }
}
